import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { submitContactForm } from '@/services/contactService';
import { useContactForm } from '@/shared/hooks/useContactForm';
import { useToast } from '@/components/ui/use-toast';

export default function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, currentLanguage } = useI18n();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);
  
  const {
    state,
    updateField,
    isFormValid,
    resetForm
  } = useContactForm();

  const { data: formData, errors } = state;
  const isValid = isFormValid();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) {
      toast({
        title: t('contact.required_fields_error'),
        description: t('contact.required_fields_description'),
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await submitContactForm({ ...formData, language: currentLanguage });
      if (response.success) {
        setIsSuccess(true);
        toast({
          title: t('contact.success_title'),
          description: t('contact.success_description'),
          variant: 'default'
        });
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (error) {
      toast({
        title: t('contact.error_title'),
        description: t('contact.error_description'),
        variant: 'destructive'
      });
    }
  };

  const handleSendNewEmail = () => {
    setIsSuccess(false);
    resetForm();
  };

  const handleBack = () => {
    if (user) {
      // Se logado, vai para home
      navigate('/home');
    } else {
      // Se não logado, vai para sign in/signup
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  if (isSuccess) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] text-white">
          
          {/* Header */}
          <div className="pt-16">
            <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
              <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-white truncate max-w-[60%]">{t('contact.title')}</h1>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="max-w-md mx-auto text-center">
                {/* Success Messages */}
                <h1 className="text-2xl font-bold text-white mb-4">
                  {t('contact.success_title')}
                </h1>
                <p className="text-gray-300 mb-8">
                  {t('contact.success_description')}
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-col space-y-4">
                  <button
                    onClick={handleSendNewEmail}
                    className="w-full px-6 py-4 bg-sossiety-red text-white rounded-full font-semibold hover:bg-sossiety-red-hover transition"
                  >
{t('contact.send_button')}
                  </button>
                  <button
                    onClick={handleGoHome}
                    className="w-full px-6 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition"
                  >
{t('contact.back_to_home')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopNavigation />
      <div className="min-h-screen bg-[#160C0C] text-white">
        
        {/* Header */}
        <div className="pt-16">
          <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
            <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-white truncate max-w-[60%]">{t('contact.title')}</h1>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubmit} className="w-full space-y-6">
                {/* Header */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-white mb-2">{t('contact.subtitle')}</h2>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      {t('contact.name_label')} *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sossiety-red focus:border-transparent"
                      placeholder={t('contact.name_placeholder')}
                    />
                    {errors.name && (
                      <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      {t('contact.email_label')} *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sossiety-red focus:border-transparent"
                      placeholder={t('contact.email_placeholder')}
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Subject Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      {t('contact.subject_label')} *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => updateField('subject', e.target.value)}
                      maxLength={60}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sossiety-red focus:border-transparent"
                      placeholder={t('contact.subject_placeholder')}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.subject && (
                        <p className="text-red-400 text-sm">{errors.subject}</p>
                      )}
                      <p className="text-gray-400 text-sm ml-auto">
                        {formData.subject.length}/60
                      </p>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      {t('contact.message_label')} *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      maxLength={2000}
                      rows={6}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sossiety-red focus:border-transparent resize-none"
                      placeholder={t('contact.message_placeholder')}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.description && (
                        <p className="text-red-400 text-sm">{errors.description}</p>
                      )}
                      <p className="text-gray-400 text-sm ml-auto">
                        {formData.description.length}/2000
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4 pb-16">
                  <button
                    type="submit"
                    disabled={!isValid}
                    className="w-full px-6 py-4 bg-sossiety-red text-white rounded-full font-semibold hover:bg-sossiety-red-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
{t('contact.send_button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </>
  );
}